import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  applyActionCode,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  getRedirectResult,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { getEmailVerificationContinueUrl } from '../lib/emailVerification';
import { isAuthNetworkError } from '../lib/authErrorMessages';

const IS_DEV = import.meta.env.DEV;

const AuthContext = createContext(null);

// getRedirectResult() is a one-time read. Call it only once per page load so
// React Strict Mode double-mount doesn't consume the result on the second run.
let redirectResultPromise = null;
function getRedirectResultOnce() {
  if (redirectResultPromise === null) {
    redirectResultPromise = getRedirectResult(auth);
  }
  return redirectResultPromise;
}

function getEmailVerificationActionCodeSettings() {
  return {
    url: getEmailVerificationContinueUrl(),
    handleCodeInApp: false,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Bumped after reload() so consumers re-render when emailVerified flips on same User reference */
  const [authRefreshKey, setAuthRefreshKey] = useState(0);
  useEffect(() => {
    let cancelled = false;

    // Admin CRM "Login as user": if URL has #adminLoginToken=..., sign in with custom token and clear hash
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const tokenMatch = hash.match(/#adminLoginToken=([^&]+)/);
    if (tokenMatch && tokenMatch[1]) {
      const token = decodeURIComponent(tokenMatch[1]);
      signInWithCustomToken(auth, token)
        .then(() => {
          if (typeof window !== 'undefined') {
            const u = new URL(window.location.href);
            u.hash = '';
            window.history.replaceState(null, '', u.pathname + u.search);
          }
        })
        .catch((err) => {
          console.error('[Auth] Admin login token failed:', err?.message || err);
          if (isAuthNetworkError(err)) {
            try { window.dispatchEvent(new CustomEvent('connection-error', { detail: { code: err?.code } })); } catch (_) {}
          }
        });
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!cancelled) setUser(firebaseUser);
    });

    // Complete email verification when user lands with ?mode=verifyEmail&oobCode=...
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const verifyMode = params?.get('mode');
    const oobCode = params?.get('oobCode');
    if (verifyMode === 'verifyEmail' && oobCode) {
      applyActionCode(auth, oobCode)
        .then(async () => {
          if (cancelled) return;
          try {
            if (auth.currentUser) await reload(auth.currentUser);
          } catch (_) { /* may be no session in this tab */ }
          if (typeof window !== 'undefined') {
            const u = new URL(window.location.href);
            u.searchParams.delete('mode');
            u.searchParams.delete('oobCode');
            u.searchParams.delete('apiKey');
            u.searchParams.delete('lang');
            window.history.replaceState({}, '', u.pathname + u.search + u.hash);
          }
          if (!cancelled) {
            setAuthRefreshKey((k) => k + 1);
            if (auth.currentUser) setUser(auth.currentUser);
          }
        })
        .catch((err) => {
          if (IS_DEV) console.warn('[Auth] Email verification link handling:', err?.code || err?.message || err);
        });
    }

    // Fallback: if redirect result never settles (e.g. stale session), stop loading after 12s so app doesn't hang
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 12000);

    getRedirectResultOnce()
      .then((credential) => {
        if (IS_DEV && credential) {
          console.log('[Auth] Google redirect sign-in succeeded', credential.user?.email);
        }
      })
      .catch((err) => {
        if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
          console.error('[Auth] Redirect result failed:', err?.code || err.message || err);
          if (isAuthNetworkError(err)) {
            try { window.dispatchEvent(new CustomEvent('connection-error', { detail: { code: err?.code } })); } catch (_) {}
          }
        }
      })
      .finally(() => {
        clearTimeout(fallbackTimer);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, []);

  const createUserDocument = async (user, userData = {}) => {
    if (!user) {
      console.error('[Auth] createUserDocument called with no user');
      return false;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const existing = userSnap.data();
        let firstName = typeof userData.firstName === 'string' ? userData.firstName.trim() : '';
        let lastName = typeof userData.lastName === 'string' ? userData.lastName.trim() : '';
        if (!firstName && user.displayName) {
          const parts = user.displayName.trim().split(/\s+/);
          firstName = parts[0] || '';
          if (!lastName && parts.length > 1) lastName = parts.slice(1).join(' ');
        }
        const patch = {};
        if (firstName && !String(existing.firstName || '').trim()) patch.firstName = firstName;
        if (lastName && !String(existing.lastName || '').trim()) patch.lastName = lastName;
        if (Object.keys(patch).length > 0) {
          patch.updatedAt = serverTimestamp();
          await updateDoc(userRef, patch);
        }
        return false;
      }
      
      const {
        firstName = '',
        lastName = '',
        subscriptionTier = 'premium',
        billingCycle = 'monthly',
        isOneTimeUse = false,
        preferredLanguage = '',
        referralSource = '',
        referralSourceDetail = '',
        freeGiftOffer = '',
      } = userData;

      const monthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
      /** No paid entitlements until Stripe checkout / one-off payment. UI applies freeDraft from app config for composing. */
      const subscription = {
        logo: false,
        maxSignatures: 0,
        verification: false,
        attachments: false,
        maxAttachments: 0,
        maxAttachmentBytes: 0,
        maxCharacters: 0,
        maxAgreementSizeKb: 0,
        maxEmailRecipients: 0,
        maxTemplates: 0,
        maxEmailsPerMonth: 0,
        maxEmailsSignedPerMonth: 0,
        maxSignedAgreementsPerMonth: 0,
        currentEmailsSignedPerMonth: 0,
        pdfExtract: false,
        templates: false,
        EmailEvents: false,
        PrioritySupportEmail: false,
        CurrentEmailsPerMonth: 0,
        CurrentEmailRecipients: 0,
        CurrentTotalTemplates: 0,
        CurrentTotalSignatures: 0,
        maxEmailSignedSignaturesPerMonth: 0,
        monthKey,
        topUpEmails: 0,
        topUpSignedAgreements: 0,
        currentTotalSignatures: 0,
      };

      const newUser = {
        uid: user.uid,
        email: user.email,
        firstName,
        lastName,
        phone: userData?.phone ?? '',
        status: 'unpaid',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        subscriptionTier,
        subscriptionId: '',
        customerId: '',
        trialUsed: false,
        billingCycle,
        isOneTimeUse,
        currentPeriodEnd: null,
        templateCount: 0,
        documentCount: 0,
        subscription,
        features: {
          richText: true,
          pdfPreview: true,
          emailDelivery: true,
        },
        /** Free sending now requires an active 7-day trial or paid subscription. */
        freeDocumentUsed: true,
        /** User's preferred language at signup (e.g. 'en', 'es') – used for localized welcome emails. */
        preferredLanguage: typeof preferredLanguage === 'string' ? preferredLanguage.trim().slice(0, 10) : '',
        /** Signup attribution (email/password flow). */
        referralSource: typeof referralSource === 'string' ? referralSource.trim().slice(0, 120) : '',
        referralSourceDetail: typeof referralSourceDetail === 'string' ? referralSourceDetail.trim().slice(0, 500) : '',
        /** Set only for the Halloween gift signup. The welcome trigger emails the code from this. */
        ...(freeGiftOffer === 'halloween-20' ? { freeGiftOffer: 'halloween-20' } : {}),
        /** When true, sign-request emails are also Bcc'd to the "from" address (server-enforced). */
        sendEmailTo: false,
      };

      await setDoc(userRef, newUser);
      if (IS_DEV) console.log('[Auth] User document created successfully');
      return true;
    } catch (error) {
      console.error('[Auth] Error creating user document:', error);
      throw error; // Re-throw so the UI knows it failed
    }
  };

  const signUp = useCallback(async (email, password, userData) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const displayName = [userData.firstName, userData.lastName].filter(Boolean).join(' ');
    
    if (displayName && cred.user) {
      await updateProfile(cred.user, { displayName });
    }

    // Create user document in Firestore
    await createUserDocument(cred.user, userData);

    return cred;
  }, []);

  const login = useCallback(async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOut = useCallback(async () => {
    return firebaseSignOut(auth);
  }, []);

  const signInWithGoogle = useCallback(async (userData = {}) => {
    const provider = new GoogleAuthProvider();
    // Use popup so the result is returned in-app; no redirect/cookie domain issues.
    const result = await signInWithPopup(auth, provider);
    
    // Create user document if it doesn't exist
    if (result.user) {
      // For Google sign-in, we might not have firstName/lastName if not provided in userData.
      // We can try to split displayName if userData is empty.
      let finalUserData = { ...userData };
      if (!finalUserData.firstName && result.user.displayName) {
        const parts = result.user.displayName.split(' ');
        finalUserData.firstName = parts[0];
        finalUserData.lastName = parts.slice(1).join(' ');
      }
      
      const created = await createUserDocument(result.user, finalUserData);
      const additional = getAdditionalUserInfo(result);
      result.isNewUser = created === true || additional?.isNewUser === true;
    }
    
    return result;
  }, []);

  const signInAnonymouslyForQuickSend = useCallback(async () => {
    const result = await signInAnonymously(auth);
    if (result.user) {
      await createUserDocument(result.user, {
        subscriptionTier: 'premium',
        billingCycle: 'monthly',
        isOneTimeUse: true,
      });
    }
    return result;
  }, []);

  const resetPassword = useCallback(async (email) => {
    return sendPasswordResetEmail(auth, email);
  }, []);

  const checkEmailExists = useCallback(async (email) => {
    if (!email || !email.trim()) return false;
    const methods = await fetchSignInMethodsForEmail(auth, email.trim());
    return Array.isArray(methods) && methods.length > 0;
  }, []);

  const sendVerificationEmail = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) throw new Error('No signed-in user');
    await sendEmailVerification(u, getEmailVerificationActionCodeSettings());
  }, []);

  const reloadAuthUser = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return null;
    await reload(u);
    setAuthRefreshKey((k) => k + 1);
    setUser(auth.currentUser);
    return auth.currentUser;
  }, []);

  const value = {
    user,
    loading,
    authRefreshKey,
    signUp,
    login,
    signOut,
    signInWithGoogle,
    signInAnonymouslyForQuickSend,
    resetPassword,
    checkEmailExists,
    sendVerificationEmail,
    reloadAuthUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
